import { Controller } from "@hotwired/stimulus";

const PULL_THRESHOLD = 72;
const MAX_PULL_DISTANCE = 120;
const PULL_RESISTANCE = 0.5;

interface PwaInstallElement extends Element {
  showDialog?: () => void;
  install?: () => void;
}

export default class extends Controller {
  declare indicatorElement?: HTMLDivElement;

  private cleanupController?: AbortController;
  private activeScrollRoot?: HTMLElement;
  private standaloneMode = false;
  private startX = 0;
  private startY = 0;
  private pullDistance = 0;
  private trackingPull = false;
  private pullReady = false;
  private refreshing = false;
  private rafId?: number;

  connect() {
    this.syncStandaloneMode();
  }

  disconnect() {
    this.disablePullToRefresh();
  }

  async install(event: Event) {
    event.preventDefault();
    const pwaInstallElement = document.querySelector(
      "pwa-install",
    ) as PwaInstallElement | null;

    if (pwaInstallElement) {
      pwaInstallElement.showDialog();
      pwaInstallElement.install();
    }
  }

  private enablePullToRefresh() {
    if (this.cleanupController) {
      return;
    }

    this.ensureIndicator();
    this.cleanupController = new AbortController();

    document.addEventListener("touchstart", this.onTouchStart, {
      capture: true,
      passive: true,
      signal: this.cleanupController.signal,
    });

    document.addEventListener("touchmove", this.onTouchMove, {
      capture: true,
      passive: false,
      signal: this.cleanupController.signal,
    });

    document.addEventListener("touchend", this.onTouchEnd, {
      capture: true,
      passive: true,
      signal: this.cleanupController.signal,
    });

    document.addEventListener("touchcancel", this.onTouchCancel, {
      capture: true,
      passive: true,
      signal: this.cleanupController.signal,
    });
  }

  private disablePullToRefresh() {
    this.cleanupController?.abort();
    this.cleanupController = undefined;

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }

    this.resetPullState();
    this.indicatorElement?.remove();
    this.indicatorElement = undefined;
  }

  private ensureIndicator() {
    if (this.indicatorElement) {
      return;
    }

    const indicatorElement = document.createElement("div");
    indicatorElement.className = "budgeter-pwa-pull-indicator";
    indicatorElement.setAttribute("aria-hidden", "true");
    indicatorElement.setAttribute("data-budgeter-pwa-pull-state", "idle");
    indicatorElement.innerHTML = `
      <div class="budgeter-pwa-pull-indicator__spinner"></div>
      <div class="budgeter-pwa-pull-indicator__label" data-budgeter-pwa-pull-label>Pull to refresh</div>
    `;

    document.body.appendChild(indicatorElement);
    this.indicatorElement = indicatorElement;
  }

  private syncStandaloneMode() {
    this.standaloneMode = this.isStandaloneMode();

    this.element.setAttribute(
      "data-budgeter-pwa-mode",
      this.standaloneMode ? "standalone" : "browser",
    );

    if (this.standaloneMode) {
      this.enablePullToRefresh();
    } else {
      this.disablePullToRefresh();
    }
  }

  private isStandaloneMode() {
    const standaloneNavigator = navigator as Navigator & {
      standalone?: boolean;
    };

    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      standaloneNavigator.standalone === true
    );
  }

  private onTouchStart = (event: TouchEvent) => {
    if (this.refreshing || event.touches.length !== 1) {
      return;
    }

    // Quick's focused workspace scrolls the document. Its input/gutter lanes
    // must not turn into pull-to-refresh when an inner scroll pane is absent.
    // Other pages keep the normal refresh contract; capture must opt out here.
    const target = this.eventTargetElement(event.target);
    if (target?.closest('[data-controller~="quick-categorisation"]')) {
      this.cancelPull();
      return;
    }
    const singleCardBody = target?.closest('[data-quick-single="true"]')
      && target.closest("[data-quick-card-background], [data-quick-card-drag]")
      && !target.closest("button, a, input, select, textarea, [data-quick-scroll]");
    if (target?.closest("[data-quick-gesture]") || singleCardBody) {
      this.cancelPull();
      return;
    }

    const scrollRoot = this.resolveScrollRoot();
    if (!this.isAtTop(scrollRoot)) {
      return;
    }

    if (this.findNestedScrollableAncestor(event.target, scrollRoot)) {
      return;
    }

    const touch = event.touches[0];
    this.activeScrollRoot = scrollRoot;
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.trackingPull = true;
    this.pullReady = false;
    this.pullDistance = 0;

    this.updatePullState("idle");
    this.updateIndicator(0, "Pull to refresh");
  };

  private onTouchMove = (event: TouchEvent) => {
    if (!this.trackingPull || this.refreshing) {
      return;
    }

    const scrollRoot = this.activeScrollRoot ?? this.resolveScrollRoot();

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.startX;
    const deltaY = touch.clientY - this.startY;

    if (deltaY <= 0) {
      this.cancelPull();
      return;
    }

    if (Math.abs(deltaX) > deltaY) {
      this.cancelPull();
      return;
    }

    if (!this.isAtTop(scrollRoot)) {
      this.cancelPull();
      return;
    }

    if (this.findNestedScrollableAncestor(event.target, scrollRoot)) {
      this.cancelPull();
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.pullDistance = Math.min(MAX_PULL_DISTANCE, deltaY * PULL_RESISTANCE);

    if (!this.rafId) {
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = undefined;

        const nextReady = this.pullDistance >= PULL_THRESHOLD;
        if (nextReady !== this.pullReady) {
          this.pullReady = nextReady;
          navigator.vibrate?.(6);
          this.updatePullState(nextReady ? "ready" : "idle");
        }

        this.updateIndicator(
          this.pullDistance,
          nextReady ? "Release to refresh" : "Pull to refresh",
        );
      });
    }
  };

  private onTouchEnd = () => {
    if (!this.trackingPull) {
      return;
    }

    this.trackingPull = false;

    if (!this.pullReady) {
      this.resetPullState();
      return;
    }

    this.refreshing = true;
    this.updatePullState("refreshing");
    this.updateIndicator(PULL_THRESHOLD, "Refreshing…");

    window.setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  private onTouchCancel = () => {
    this.cancelPull();
  };

  private cancelPull() {
    if (!this.trackingPull || this.refreshing) {
      return;
    }

    this.trackingPull = false;
    this.resetPullState();
  }

  private resetPullState() {
    this.activeScrollRoot = undefined;
    this.pullDistance = 0;
    this.pullReady = false;
    this.refreshing = false;
    this.updatePullState("idle");
    this.updateIndicator(0, "Pull to refresh");
  }

  private resolveScrollRoot() {
    const appShellElement = document.getElementById("main");
    if (
      appShellElement instanceof HTMLElement &&
      this.isScrollableElement(appShellElement)
    ) {
      return appShellElement;
    }

    const scrollingElement = document.scrollingElement;
    if (scrollingElement instanceof HTMLElement) {
      return scrollingElement;
    }

    return document.documentElement;
  }

  private isAtTop(element: HTMLElement) {
    return element.scrollTop <= 0;
  }

  private findNestedScrollableAncestor(
    target: EventTarget | null,
    scrollRoot: HTMLElement,
  ) {
    let element = this.eventTargetElement(target);

    while (element && element !== scrollRoot && element !== document.body) {
      if (element instanceof HTMLElement && this.isScrollableElement(element)) {
        return element;
      }

      element = element.parentElement;
    }

    return null;
  }

  private eventTargetElement(target: EventTarget | null) {
    if (target instanceof Element) {
      return target;
    }

    if (target instanceof Text) {
      return target.parentElement;
    }

    return null;
  }

  private isScrollableElement(element: HTMLElement) {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;

    if (!["auto", "scroll", "overlay"].includes(overflowY)) {
      return false;
    }

    return element.scrollHeight > element.clientHeight + 1;
  }

  private updatePullState(state: "idle" | "ready" | "refreshing") {
    this.indicatorElement?.setAttribute(
      "data-budgeter-pwa-pull-state",
      state,
    );
  }

  private updateIndicator(distance: number, label: string) {
    if (!this.indicatorElement) {
      return;
    }

    this.indicatorElement.style.setProperty(
      "--budgeter-pwa-pull-offset",
      `${distance}px`,
    );

    const opacity = Math.min(1, distance / (PULL_THRESHOLD * 0.65));
    this.indicatorElement.style.setProperty(
      "--budgeter-pwa-pull-opacity",
      `${opacity}`,
    );

    const labelElement = this.indicatorElement.querySelector(
      "[data-budgeter-pwa-pull-label]",
    );

    if (labelElement) {
      labelElement.textContent = label;
    }
  }
}
